export type Persona =
  | 'first_timer'
  | 'self_learner'
  | 'csoc_class_day'
  | 'csoc_rest_day';

export interface Webinar {
  webinar_id: number;
  webinar_time: string;
  webinar_date: string;
  topic: string;
  join_url: string;
  // Present on tournament/v1/user_webinar_detail rows; used by the curriculum
  // panel to order sessions. Not every row carries one.
  session_number?: number;
  // Backend's own computed status (webinar_util.py) — 'cancelled' for a
  // cancelled occurrence regardless of date, else 'completed' | 'updating'
  // for a past one or 'scheduled' for a future one. 'updating' is a past
  // session the held/cancelled sync hasn't settled on yet.
  status?: 'completed' | 'cancelled' | 'scheduled' | 'updating' | string;
  was_cancelled?: boolean;
  was_held?: boolean;
  // True when a late/over-limit cancellation still counts against the
  // subscription (see Meetings.tsx) — the class didn't happen, but it isn't
  // "free" either. Cancelled either way for display purposes here.
  held_as_penalty?: boolean;
}

export interface ClassifiedWebinars {
  today: Webinar | null;
  next: Webinar | null;
}

// Local calendar day as YYYY-MM-DD. The webinar API returns `webinar_date` in
// exactly this form, and the rest of the app compares it as a string (see
// Meetings.tsx's today/tomorrow check). Doing the same avoids the timezone bug
// in `new Date('2026-07-29')`, which parses as UTC midnight and lands on the
// previous day for anyone behind UTC.
function localDateKey(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

// Normalises a webinar_date to YYYY-MM-DD, tolerating a dd-mm-yyyy variant.
function dateKeyOf(dateStr?: string): string | null {
  if (!dateStr) return null;
  const trimmed = dateStr.trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const m = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}

// Buckets a raw webinar list into "today" (class happening today) and "next"
// (soonest upcoming), dropping anything in the past. ISO date keys compare
// correctly as plain strings, so no Date maths is needed.
export function classifyWebinars(
  list: Webinar[] | undefined | null,
): ClassifiedWebinars {
  const todayKey = localDateKey(new Date());
  let today: Webinar | null = null;
  let next: Webinar | null = null;
  let nextKey: string | null = null;

  (list || []).forEach(webinar => {
    // A cancelled occurrence still has a real webinar_date, so without this
    // it could win "today"/"next" and the hero card would tell a CSOC
    // student to join a class that isn't happening.
    if (webinar?.status === 'cancelled' || webinar?.was_cancelled) return;
    const key = dateKeyOf(webinar?.webinar_date);
    if (!key) return;
    if (key === todayKey) {
      today = webinar;
      return;
    }
    if (key < todayKey) return; // past, ignore
    if (!nextKey || key < nextKey) {
      nextKey = key;
      next = webinar;
    }
  });

  return {today, next};
}

// Beginner (player_type 1) always wins; otherwise a class today/soon makes
// the user "CSOC", falling back to a plain self-directed learner.
export function pickPersona(
  isBeginnerPlayer: boolean,
  classified: ClassifiedWebinars,
): Persona {
  if (isBeginnerPlayer) return 'first_timer';
  if (classified.today) return 'csoc_class_day';
  if (classified.next) return 'csoc_rest_day';
  return 'self_learner';
}

// ---- Persona-varying copy, translated from DashboardRevampContent.dc.html's
// own bannerSets/heroByPersona objects (structure only — real stats/club
// data are never hardcoded, only UI copy/labels). ----

export const HERO_COPY_BY_PERSONA: Record<
  Persona,
  {title: string; sub: string; ctaTitle: string}
> = {
  first_timer: {
    title: 'Play your first game',
    sub: 'Friendly game · matched with another beginner',
    ctaTitle: 'Start a beginner match',
  },
  self_learner: {
    title: 'Play Online',
    sub: 'Randomize opponent or challenge a friend',
    ctaTitle: 'Start a new game',
  },
  csoc_class_day: {
    title: 'Warm up before class',
    sub: 'One quick game, then join your coach',
    ctaTitle: 'Start a quick game',
  },
  csoc_rest_day: {
    title: 'Play Online',
    sub: 'Your usual game, one tap',
    ctaTitle: 'Start a new game',
  },
};

// Short human label for the resolved persona — surfaced in the hero so which
// variant is active is actually visible (previously the only difference was
// hero copy, and self_learner's copy matched the old hardcoded text exactly,
// so the persona engine looked like it wasn't running at all).
export const PERSONA_LABEL: Record<Persona, string> = {
  first_timer: 'First game',
  self_learner: 'Self-paced',
  csoc_class_day: 'Class today',
  csoc_rest_day: 'Rest day',
};

// Club-card subtitle per persona — the design's own `clubByPersona.subtitle`.
export const CLUB_SUBTITLE_BY_PERSONA: Record<Persona, string> = {
  first_timer: 'Start with the beginner-friendly ones',
  self_learner: 'What your club is up to this week',
  csoc_class_day: 'Class first, then the club events',
  csoc_rest_day: 'No class today — pick something up',
};

export function formatNextClassLabel(webinar: Webinar | null): string {
  if (!webinar) return '';
  const key = dateKeyOf(webinar.webinar_date);
  if (!key) return webinar.topic || '';
  // Build from the date parts so the label shows the class's own calendar day,
  // not a UTC-shifted one.
  const [y, m, d] = key.split('-').map(Number);
  const day = new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return `${day}${webinar.webinar_time ? ` · ${webinar.webinar_time}` : ''}`;
}
